import { useEffect, useMemo, useState } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import IncidentReportModal from '../../components/quality/IncidentReportModal';
import { ACTION_INFO, reportDeadline, reportOverdue, hoursLeftToReport } from '../../lib/incidents';

// Escalating severity — warn → copper → danger — reusing the existing status
// ramp instead of two extra one-off ambers/oranges.
const CATEGORY_INFO = ACTION_INFO;


// Types that typically require reporting to DCH/HFRD within 24h (auto-suggested).

const subjectName = (inc) =>
  inc.subject_type === 'client' ? (inc.clients ? `${inc.clients.last_name}, ${inc.clients.first_name}` : '—')
    : inc.subject_type === 'operational' ? 'Operational'
    : (inc.staff?.last_name ? `${inc.staff.last_name}, ${inc.staff.first_name}` : inc.staff?.full_name || '—');


export default function DisciplinaryLog() {
  const { profile } = useAuth();
  // Coordinators and nurses may file and read. Coordinators also work the
  // incidents they can see — mark reported, close, reopen — but what they can
  // see is only the client-related ones: a colleague's staff disciplinary file
  // stays with managers, enforced in RLS on staff_incidents, not just here.
  // Voiding a record stays with managers on any incident.
  const isManager = ['admin', 'manager'].includes(profile?.role);
  const canWork = isManager || profile?.role === 'coordinator';
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [sortKey, setSortKey] = useState('incident_date');
  const [sortAsc, setSortAsc] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [ackName, setAckName] = useState('');
  const { toast, showToast, hideToast } = useToast(4000);


  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('staff_incidents')
      .select('*, staff(full_name, first_name, last_name), clients(first_name, last_name, client_id)')
      .order('incident_date', { ascending: false });
    if (error) showToast('Failed to load: ' + error.message, 'error');
    setIncidents(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const voidIncident = async (inc) => {
    const why = window.prompt('Why does this record not belong in the incident log?');
    if (why === null) return;
    if (!why.trim()) { showToast('Give a reason.', 'error'); return; }
    const { error } = await supabase.from('staff_incidents')
      .update({ status: 'void', void_reason: why.trim(), updated_at: new Date().toISOString() }).eq('id', inc.id);
    if (error) { showToast('Could not void: ' + error.message, 'error'); return; }
    setSelected(prev => (prev ? { ...prev, status: 'void', void_reason: why.trim() } : prev));
    showToast('Record voided — kept for the audit trail, out of the counts.');
    load();
  };

  const markReported = async (inc) => {
    const { error } = await supabase.from('staff_incidents').update({
      reported_external: true, reported_external_at: new Date().toISOString(), reported_external_by: profile?.full_name || null,
    }).eq('id', inc.id);
    if (error) { showToast('Failed: ' + error.message, 'error'); return; }
    setSelected(prev => prev ? { ...prev, reported_external: true, reported_external_at: new Date().toISOString() } : prev);
    showToast('Marked reported to DCH.');
    load();
  };

  const closeIncident = async (inc) => {
    const { error } = await supabase.from('staff_incidents').update({ status: 'closed', updated_at: new Date().toISOString() }).eq('id', inc.id);
    if (error) { showToast('Failed: ' + error.message, 'error'); return; }
    setSelected(null);
    showToast('Incident closed.');
    load();
  };

  // Both "Mark reported to DCH" and "Mark Closed" were previously one-way —
  // a mis-click on the 24h regulatory reporting flag, or closing the wrong
  // incident, had no in-app correction path at all.
  const unmarkReported = async (inc) => {
    if (!window.confirm('Undo "Reported to DCH"? Only do this if it was marked by mistake — this incident is still 24h-reportable.')) return;
    const { error } = await supabase.from('staff_incidents').update({
      reported_external: false, reported_external_at: null, reported_external_by: null,
    }).eq('id', inc.id);
    if (error) { showToast('Failed: ' + error.message, 'error'); return; }
    setSelected(prev => prev ? { ...prev, reported_external: false, reported_external_at: null } : prev);
    showToast('Reported-to-DCH mark undone.');
    load();
  };

  const reopenIncident = async (inc) => {
    if (!window.confirm('Reopen this incident?')) return;
    const { error } = await supabase.from('staff_incidents').update({ status: 'open', updated_at: new Date().toISOString() }).eq('id', inc.id);
    if (error) { showToast('Failed: ' + error.message, 'error'); return; }
    setSelected(prev => prev ? { ...prev, status: 'open' } : prev);
    showToast('Incident reopened.');
    load();
  };

  const acknowledge = async (inc) => {
    if (!ackName.trim()) { showToast('Type the staff member\'s name to acknowledge.', 'error'); return; }
    const { error } = await supabase.from('staff_incidents').update({
      staff_acknowledged: true, acknowledged_name: ackName.trim(), acknowledged_at: new Date().toISOString(),
    }).eq('id', inc.id);
    if (error) { showToast('Failed: ' + error.message, 'error'); return; }
    setAckName('');
    setSelected(prev => prev ? { ...prev, staff_acknowledged: true, acknowledged_name: ackName.trim() } : prev);
    showToast('Acknowledged.');
    load();
  };

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const filtered = incidents
    .filter(i => (filter === 'All' ? i.status !== 'void' : i.status === filter))
    .sort((a, b) => {
      let av = sortKey === 'staff' ? subjectName(a) : a[sortKey];
      let bv = sortKey === 'staff' ? subjectName(b) : b[sortKey];
      if (av == null) return 1; if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortAsc ? cmp : -cmp;
    });

  const live = incidents.filter(i => i.status !== 'void');
  const overdueReports = incidents.filter(reportOverdue).length;
  const openCount = incidents.filter(i => i.status === 'open').length;
  const closedCount = incidents.filter(i => i.status === 'closed').length;

  const columns = [
    {
      id: 'reference_no', header: 'Ref #', size: 90, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)' }}>{info.row.original.reference_no || '—'}</span>,
    },
    {
      id: 'staff', header: 'Subject', size: 160,
      meta: { mobile: 'primary' },
      cell: info => {
        const inc = info.row.original;
        return (
          <span>
            {subjectName(inc)}
            <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6, textTransform: 'capitalize' }}>{inc.subject_type || 'staff'}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'incident_date', header: 'Date', size: 100,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ fontSize: 12 }}>{info.getValue()}</span>,
    },
    {
      id: 'incident_type', header: 'Incident Type', size: 190, enableSorting: false,
      meta: { mobile: 'primary' },
      cell: info => {
        const inc = info.row.original;
        return (
          <span style={{ fontSize: 12 }}>
            {inc.incident_type || '—'}
            {inc.reportable && (
              reportOverdue(inc)
                ? <span title="24h DCH reporting window passed — not yet reported" style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--danger)' }}>⚠ report overdue</span>
                : inc.reported_external
                  ? <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--success)' }}>✓ reported</span>
                  : <span title="Reportable to DCH within 24h" style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--warn)', background: 'var(--warn-bg)', borderRadius: 8, padding: '1px 6px' }}>24h</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'category', header: 'Action', size: 130, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const cat = CATEGORY_INFO[info.row.original.category] || CATEGORY_INFO.other;
        return <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: cat.bg, color: cat.color }}>{cat.label}</span>;
      },
    },
    {
      accessorKey: 'description', header: 'Description', size: 260, enableSorting: false,
      meta: { truncate: true, mobile: 'hidden' },
      cell: info => info.getValue(),
    },
    {
      id: 'acknowledged', header: 'Acknowledged', size: 150, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const inc = info.row.original;
        return inc.staff_acknowledged ? <span style={{ fontSize: 12, color: 'var(--success)' }}>✓ {inc.acknowledged_name}</span> : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>;
      },
    },
    {
      accessorKey: 'status', header: 'Status', size: 110,
      meta: { mobile: 'primary' },
      cell: info => {
        const status = info.getValue();
        return (
          <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: status === 'open' ? 'var(--warn-bg)' : 'var(--success-bg)', color: status === 'open' ? 'var(--warn)' : 'var(--success)' }}>
            {status === 'open' ? '● Open' : '✓ Closed'}
          </span>
        );
      },
    },
    {
      id: 'actions', header: '', size: 80, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: () => <span style={{ color: 'var(--accent2)', fontSize: 12 }}>View →</span>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Total incidents', value: live.length, tone: 'neutral' },
          { label: 'Open', value: openCount, tone: openCount > 0 ? 'warn' : 'neutral' },
          { label: 'Closed', value: closedCount, tone: 'neutral' },
          { label: 'Past 24h reporting deadline', value: overdueReports, tone: overdueReports > 0 ? 'alert' : 'neutral' },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Incident Log</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {['All', 'open', 'closed', 'void'].map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: filter === f ? 'var(--accent)' : 'transparent', color: filter === f ? '#fff' : 'var(--text3)', borderColor: filter === f ? 'var(--accent)' : 'var(--border)' }}>
                  {f === 'All' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <button onClick={() => setShowModal(true)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}>+ Log Incident</button>
          </div>
        </div>

        <DataTable
          tableId="disciplinary-log"
          columns={columns}
          data={filtered}
          getRowId={i => i.id}
          onRowClick={inc => setSelected(inc)}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage={`No incidents logged${filter !== 'All' ? ` (${filter})` : ''}.`}
          maxHeight="calc(100vh - 320px)"
        />
      </div>

      {showModal && (
        <IncidentReportModal
          onClose={() => setShowModal(false)}
          onFiled={() => { setShowModal(false); load(); }}
        />
      )}

      {selected && (() => {
        const cat = CATEGORY_INFO[selected.category] || CATEGORY_INFO.other;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => { setSelected(null); setAckName(''); }}>
            <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 480, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{subjectName(selected)}</div>
                <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: cat.bg, color: cat.color }}>{cat.label}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>
                <span style={{ fontFamily: 'monospace' }}>{selected.reference_no || '—'}</span> · {selected.incident_date} · Reported by {selected.reported_by || '—'}
              </div>
              {selected.incident_type && <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500, marginBottom: 14 }}>{selected.incident_type}</div>}
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.6 }}>{selected.description}</div>
              {selected.action_taken && (
                <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--bg2)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                  <strong>Action taken:</strong> {selected.action_taken}
                </div>
              )}
              {selected.follow_up_date && (
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>Follow-up: {selected.follow_up_date}</div>
              )}

              {selected.status === 'void' && (
                <div style={{ fontSize: 12, borderRadius: 8, padding: '10px 12px', marginBottom: 14, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
                  <b>Not an incident.</b> {selected.void_reason || ''} Kept for the audit trail; excluded from the counts.
                </div>
              )}

              {selected.reportable && (
                <div style={{ fontSize: 12, borderRadius: 8, padding: '10px 12px', marginBottom: 14, background: reportOverdue(selected) ? 'var(--danger-bg)' : 'var(--warn-bg)', border: `1px solid ${reportOverdue(selected) ? 'var(--danger)' : 'var(--warn-border)'}` }}>
                  <div style={{ fontWeight: 600, color: reportOverdue(selected) ? 'var(--danger)' : 'var(--warn)', marginBottom: 4 }}>
                    DCH / HFRD reportable — within 24 hours
                  </div>
                  <div style={{ color: 'var(--text2)', marginBottom: selected.reported_external ? 0 : 8 }}>
                    Report by {reportDeadline(selected) ? reportDeadline(selected).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    {reportOverdue(selected)
                      ? <b style={{ color: 'var(--danger)' }}> — OVERDUE by {Math.abs(hoursLeftToReport(selected))}h</b>
                      : <b style={{ color: 'var(--warn)' }}> — {hoursLeftToReport(selected)}h left</b>}
                    <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>
                      Clock starts when we found out{selected.discovered_at ? ` (${new Date(selected.discovered_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})` : ''}.
                    </div>
                  </div>
                  {selected.reported_external
                    ? <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ color: 'var(--success)' }}>✓ Reported {selected.reported_external_at ? new Date(selected.reported_external_at).toLocaleDateString('en-US') : ''}</span>
                        <button onClick={() => unmarkReported(selected)} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text3)' }}>Undo</button>
                      </div>
                    : canWork && <button onClick={() => markReported(selected)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}>Mark reported to DCH</button>}
                </div>
              )}

              {selected.staff_acknowledged ? (
                <div style={{ fontSize: 12, color: 'var(--success)', marginBottom: 16 }}>✓ Acknowledged by {selected.acknowledged_name}</div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Staff acknowledgment (typed name, captured in person)</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={ackName} onChange={e => setAckName(e.target.value)} placeholder="Staff member's full name" style={{ flex: 1, border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
                    <button onClick={() => acknowledge(selected)} style={{ padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}>Sign</button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setSelected(null); setAckName(''); }} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
                {isManager && selected.status !== 'void' && (
                  <button onClick={() => voidIncident(selected)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }} title="Not an incident — keep the record, take it out of the log">Not an incident</button>
                )}
                {!canWork ? null : selected.status === 'open' ? (
                  <button onClick={() => closeIncident(selected)} style={{ flex: 2, padding: '10px 0', background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Mark Closed</button>
                ) : (
                  <button onClick={() => reopenIncident(selected)} style={{ flex: 2, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Reopen Incident</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <Toast {...(toast || {})} onClose={hideToast} />
    </div>
  );
}
